import { Users, XCircle } from 'lucide-react';
import React, { useMemo, useRef } from 'react';

import { EmptyState } from '@/components/EmptyState';
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select';
import type { SupportedTimeZone } from '@/constants/date';
import { ANALYTICS_SAVED_VIEWS_CONFIG } from '@/features/savedViews/config';
import { SavedViewsMenu } from '@/features/savedViews/SavedViewsMenu';
import { useAgentsList } from '@/pages/analytics/hooks/useAgentsList';
import { useAgentsSummary } from '@/pages/analytics/hooks/useAgentsSummary';
import { useAgentsTabFiltersState } from '@/pages/analytics/hooks/useAgentsTabFiltersState';
import { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import type { AgentsSummaryResponse } from '@/pages/analytics/types';
import {
  formatSelectedOptionLabels,
  formatSelectedProgramScope,
} from '@/pages/analytics/utils/formatProgramScope';
import { normalizeAgentsList } from '@/pages/analytics/utils/normalizeChartRows';

import { AgentsKpiCards } from './AgentsKpiCards';
import { AgentsQuickFilterChips, quickFilterToScoreRange } from './AgentsQuickFilterChips';
import { AgentsTable } from './AgentsTable';
import { AnalyticsFilters } from './AnalyticsFilters';
import { AnalyticsPdfReportHeader, formatAnalyticsReportDate } from './AnalyticsPdfReportHeader';
import { type ChartDownloadFormat, ChartDownloadMenu } from './ChartDownloadMenu';

interface ProgramOption {
  label: string;
  value: string;
}

interface AgentsPerformanceTabProps {
  organizationId: string;
  organizationName: string;
  programOptions: ProgramOption[];
  selectedProgramIds: string[];
  onProgramFilterChange: (next: string[]) => void;
  start: string;
  end: string;
  timeZone: SupportedTimeZone;
  onDateRangeChange: (range: { start: string; end: string }) => void;
}

const filterSummaryForScoreRange = (
  summary: AgentsSummaryResponse | undefined,
  scoreGte?: number,
  scoreLte?: number,
): AgentsSummaryResponse | undefined => {
  if (!summary) return undefined;

  const agents = summary.agents.filter(a => {
    if (scoreGte != null && a.avg_score < scoreGte) return false;
    if (scoreLte != null && a.avg_score > scoreLte) return false;
    return true;
  });

  const avg_score =
    agents.length > 0 ? agents.reduce((total, a) => total + a.avg_score, 0) / agents.length : 0;

  return {
    ...summary,
    total_agents: agents.length,
    avg_score,
    agents,
  };
};

const quickFilterLabel = (quickFilter: string): string => {
  switch (quickFilter) {
    case 'best':
      return 'Best Performing';
    case 'coaching':
      return 'Unsatisfactory';
    case 'all':
    default:
      return 'All';
  }
};

export const AgentsPerformanceTab: React.FC<AgentsPerformanceTabProps> = ({
  organizationId,
  organizationName,
  programOptions,
  selectedProgramIds,
  onProgramFilterChange,
  start,
  end,
  timeZone,
  onDateRangeChange,
}) => {
  const exportRef = useRef<HTMLDivElement>(null);
  const {
    agentIds,
    setAgentIds,
    quickFilter,
    setQuickFilter,
    sortBy,
    sortOrder,
    setSort,
    page,
    setPage,
    pageSize,
    setPageSize,
  } = useAgentsTabFiltersState();

  const { scoreGte, scoreLte } = useMemo(() => quickFilterToScoreRange(quickFilter), [quickFilter]);
  const reportProgramScope = useMemo(
    () => formatSelectedProgramScope(programOptions, selectedProgramIds),
    [programOptions, selectedProgramIds],
  );
  const reportDateRange = useMemo(
    () => `${formatAnalyticsReportDate(start)} - ${formatAnalyticsReportDate(end)}`,
    [end, start],
  );

  const optionsSummaryQuery = useAgentsSummary({
    organizationId,
    programIds: selectedProgramIds,
    start,
    end,
    timeZone,
  });
  const agentFilteredSummaryQuery = useAgentsSummary({
    organizationId,
    programIds: selectedProgramIds,
    agentIds,
    start,
    end,
    timeZone,
  });
  const listQuery = useAgentsList({
    organizationId,
    programIds: selectedProgramIds,
    agentIds,
    start,
    end,
    timeZone,
    scoreGte,
    scoreLte,
    sortBy,
    sortOrder,
    page,
    pageSize,
  });

  const optionsSummary = optionsSummaryQuery.data;
  const agentFilteredSummary = agentFilteredSummaryQuery.data;
  const scoreFilteredSummary = useMemo(
    () => filterSummaryForScoreRange(agentFilteredSummary, scoreGte, scoreLte),
    [agentFilteredSummary, scoreGte, scoreLte],
  );
  const list = listQuery.data;
  const pagination = list?.pagination;
  const hasNextPage = pagination ? pagination.page < pagination.total_pages : false;
  const queryError =
    optionsSummaryQuery.error || agentFilteredSummaryQuery.error || listQuery.error;

  const agentOptions = useMemo(
    () =>
      (optionsSummary?.agents ?? [])
        .map(a => ({ label: a.agent_name, value: a.agent_id }))
        .sort((l, r) => l.label.localeCompare(r.label)),
    [optionsSummary?.agents],
  );
  const reportAgentScope = useMemo(
    () => formatSelectedOptionLabels(agentOptions, agentIds, 'All agents'),
    [agentOptions, agentIds],
  );

  const download = useDownloadChart();
  const handleDownload = (format: ChartDownloadFormat): Promise<void> | undefined => {
    if (!list?.agents.length) return undefined;
    return download(
      normalizeAgentsList(list.agents, `${start} to ${end}`),
      format,
      'Agents Performance',
      format === 'pdf' ? exportRef.current : null,
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <AnalyticsFilters
        programOptions={programOptions}
        selectedProgramIds={selectedProgramIds}
        onProgramFilterChange={onProgramFilterChange}
        start={start}
        end={end}
        timeZone={timeZone}
        onDateRangeChange={onDateRangeChange}
        extraFilters={
          <SearchableMultiSelect
            options={agentOptions}
            selected={agentIds}
            onChange={setAgentIds}
            placeholder="All Agents"
            className="w-44 px-3"
            contentClassName="w-auto min-w-[220px]"
            searchPlaceholder="Search agents..."
            emptyMessage="No agents found."
          />
        }
      />

      <div className="flex items-center justify-between gap-3">
        <AgentsQuickFilterChips
          totalAgents={agentFilteredSummary?.total_agents ?? 0}
          agents={agentFilteredSummary?.agents ?? []}
          active={quickFilter}
          onChange={setQuickFilter}
        />
        <div className="flex items-center gap-3">
          <ChartDownloadMenu onDownload={handleDownload} disabled={!list?.agents.length} />
          <SavedViewsMenu config={ANALYTICS_SAVED_VIEWS_CONFIG} organizationId={organizationId} />
        </div>
      </div>

      {queryError ? (
        <EmptyState
          title="Couldn't load agents"
          description={
            queryError instanceof Error
              ? queryError.message
              : 'The analytics request failed. Please try again.'
          }
          icon={XCircle}
          action={{
            label: 'Retry',
            onClick: () => {
              optionsSummaryQuery.refetch();
              agentFilteredSummaryQuery.refetch();
              listQuery.refetch();
            },
          }}
        />
      ) : (
        <div ref={exportRef} className="flex flex-col gap-4">
          <AnalyticsPdfReportHeader
            icon={Users}
            title="Agents Performance"
            metaItems={[
              { label: 'Organization', value: organizationName || organizationId },
              { label: 'Date range', value: reportDateRange },
              { label: 'Programs', value: reportProgramScope },
              { label: 'Agents', value: reportAgentScope },
              { label: 'Segment', value: quickFilterLabel(quickFilter) },
            ]}
          />
          <AgentsKpiCards
            summary={scoreFilteredSummary}
            loading={agentFilteredSummaryQuery.isLoading || agentFilteredSummaryQuery.isFetching}
          />

          <AgentsTable
            organizationId={organizationId}
            rows={list?.agents ?? []}
            loading={listQuery.isLoading}
            currentPage={pagination?.page ?? page}
            pageSize={pageSize}
            hasNextPage={hasNextPage}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={setSort}
          />
        </div>
      )}
    </div>
  );
};
